import { useCallback, useEffect, useRef, useState } from "react";
import type { UIMessage } from "ai";
import type { Json } from "@baseline/web/database.types";
import { supabase } from "@baseline/web/supabase";
import type { PageContext } from "./usePageContext";

export interface ThreadSummary {
	id: string;
	title: string | null;
	updated_at: string;
	page_context: PageContext | null;
}

export function useChatThread(
	userId: string | undefined,
	pageContext: PageContext,
) {
	const [threadId, setThreadId] = useState<string | null>(null);
	const [initialMessages, setInitialMessages] = useState<UIMessage[]>([]);
	const [isLoading, setIsLoading] = useState(false);
	// CONTEXT: threadContext tracks the context the thread was created/loaded
	// with, so we can detect when the user navigates away mid-conversation.
	const [threadContext, setThreadContext] =
		useState<PageContext>(pageContext);
	const loadedRef = useRef(false);
	// CONTEXT: Store pageContext in a ref so saveMessages/createThread don't
	// need it in their dependency arrays (avoids re-creating on every navigation).
	const contextRef = useRef(pageContext);
	contextRef.current = pageContext;

	const loadThread = useCallback(async () => {
		if (!userId || loadedRef.current) return;
		loadedRef.current = true;
		setIsLoading(true);

		try {
			let query = supabase
				.from("chat_threads")
				.select("id, title, messages, project_id, page_context")
				.eq("user_id", userId)
				.order("updated_at", { ascending: false })
				.limit(1);

			if (pageContext.projectId) {
				query = query.eq("project_id", pageContext.projectId);
			}

			const { data } = await query;

			const thread = data?.[0];
			if (
				thread &&
				(thread.messages as unknown as UIMessage[]).length > 0
			) {
				setThreadId(thread.id);
				setInitialMessages(thread.messages as unknown as UIMessage[]);
				if (thread.page_context) {
					setThreadContext(
						thread.page_context as unknown as PageContext,
					);
				}
			} else {
				const newId = await createThread();
				if (newId) setThreadId(newId);
			}
		} finally {
			setIsLoading(false);
		}
	}, [userId, pageContext.projectId]);

	const createThread = useCallback(async (): Promise<string | null> => {
		if (!userId) return null;

		const { data, error } = await supabase
			.from("chat_threads")
			.insert({
				user_id: userId,
				project_id: contextRef.current.projectId ?? null,
				page_context: contextRef.current as unknown as Json,
				messages: [],
			})
			.select("id")
			.single();

		if (error || !data) {
			console.error(
				"createThread:",
				JSON.stringify({ error: error?.message }),
			);
			return null;
		}

		return data.id;
	}, [userId]);

	const saveMessages = useCallback(
		async (messages: UIMessage[]) => {
			if (!threadId || !userId) return;

			const title =
				messages
					.find((m) => m.role === "user")
					?.parts.find((p) => p.type === "text")
					?.text?.slice(0, 80) ?? null;

			const currentCtx = contextRef.current;
			await supabase
				.from("chat_threads")
				.update({
					messages: messages as unknown as Json,
					title,
					updated_at: new Date().toISOString(),
					page_context: currentCtx as unknown as Json,
				})
				.eq("id", threadId);

			setThreadContext(currentCtx);
		},
		[threadId, userId],
	);

	const startNewThread = useCallback(async () => {
		loadedRef.current = false;
		const newId = await createThread();
		if (newId) {
			setThreadId(newId);
			setInitialMessages([]);
			setThreadContext(contextRef.current);
		}
		return newId;
	}, [createThread]);

	const [threads, setThreads] = useState<ThreadSummary[]>([]);
	const [threadsLoading, setThreadsLoading] = useState(false);

	const loadThreadList = useCallback(async () => {
		if (!userId) return;
		setThreadsLoading(true);
		try {
			const { data } = await supabase
				.from("chat_threads")
				.select("id, title, updated_at, page_context")
				.eq("user_id", userId)
				.order("updated_at", { ascending: false })
				.limit(50);

			if (data) {
				// Only show threads that have a title (i.e. at least one message)
				setThreads(
					(data as unknown as ThreadSummary[]).filter((t) => t.title),
				);
			}
		} finally {
			setThreadsLoading(false);
		}
	}, [userId]);

	const switchThread = useCallback(
		async (id: string) => {
			if (!userId) return;

			const { data } = await supabase
				.from("chat_threads")
				.select("id, title, messages, page_context")
				.eq("id", id)
				.eq("user_id", userId)
				.single();

			if (data) {
				setThreadId(data.id);
				setInitialMessages(data.messages as unknown as UIMessage[]);
				if (data.page_context) {
					setThreadContext(
						data.page_context as unknown as PageContext,
					);
				}
			}
		},
		[userId],
	);

	useEffect(() => {
		loadThread();
	}, [loadThread]);

	return {
		threadId,
		threadContext,
		initialMessages,
		isLoading,
		saveMessages,
		startNewThread,
		threads,
		threadsLoading,
		loadThreadList,
		switchThread,
	};
}
